import { useEffect, type ReactNode } from "react";
import { Modal as RNModal, Platform, Pressable, ScrollView, Text, View } from "react-native";

import { COLORS, FONTS } from "../../lib/brand";
import { PANE_THEMES } from "../brand/paneThemes";

const STUDENT_THEME = PANE_THEMES.Student;
import Svg, { Path } from "react-native-svg";


/**
 * Header overlay primitive.
 *
 * Used by the student persistent header (Activity / Settings / Profile
 * icons). Rendered as a modal-style panel anchored to the top-right of
 * the viewport with a blurred backdrop — the page underneath is still
 * visible (just blurred) and route state is unchanged. The user closes
 * with the X, the backdrop, or the ESC key (web).
 *
 * Visual:
 *   - Backdrop: very subtle dim with a webkit/standard backdrop-filter blur
 *   - Panel: linen surface anchored top-right at ~440px max width, height
 *     capped to 78vh so the page is still partly visible
 *   - Close: small X in the panel's top-right corner
 */
export function HeaderOverlay({
  visible,
  onClose,
  title,
  subtitle,
  children,
}: {
  visible: boolean;
  onClose: () => void;
  title: string;
  subtitle?: string | null;
  children: ReactNode;
}) {
  // Web-only: close on Escape.
  useEffect(() => {
    if (Platform.OS !== "web" || !visible) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [visible, onClose]);

  const isWeb = Platform.OS === "web";

  return (
    <RNModal visible={visible} transparent animationType="none" onRequestClose={onClose}>
      {/* Backdrop — blurs the page underneath on web. The Pressable is
          pulled out of "pointer" cursor so the whole overlay surface
          doesn't read as clickable; only buttons inside (X, links, etc.)
          show the click cursor. Clicking the backdrop still closes. */}
      <Pressable
        onPress={onClose}
        accessibilityLabel="Close overlay"
        style={[
          {
            flex: 1,
            backgroundColor: "rgba(14,14,14,0.45)",
          },
          isWeb
            ? ({
                backdropFilter: "blur(10px)",
                WebkitBackdropFilter: "blur(10px)",
                cursor: "default",
              } as object)
            : null,
        ]}
      >
        {/* Panel container — centered both axes, large but not full-bleed.
            Pressable swallows backdrop clicks so clicking inside the panel
            does not close the overlay. */}
        <View
          pointerEvents="box-none"
          style={{
            flex: 1,
            alignItems: "center",
            justifyContent: "center",
            paddingHorizontal: 24,
            paddingVertical: isWeb ? 48 : 32,
          }}
        >
          <Pressable
            onPress={(e) => e.stopPropagation()}
            style={({ pressed: _ }) => [
              {
                width: "100%",
                maxWidth: 1080,
                height: "100%",
                maxHeight: isWeb ? ("85vh" as unknown as number) : 640,
                backgroundColor: COLORS.LINEN.hex,
                borderWidth: 1,
                borderColor: COLORS.SAND_DARK.hex,
                borderRadius: 8,
                shadowColor: "#000",
                shadowOpacity: 0.32,
                shadowRadius: 56,
                shadowOffset: { width: 0, height: 24 },
                overflow: "hidden",
              },
              // Override the default RN-Web pointer cursor — the panel
              // surface itself isn't clickable, so showing pointer
              // throughout misleads. Buttons inside still set their own.
              isWeb ? ({ cursor: "default" } as unknown as object) : null,
            ]}
          >
            <View
              style={{
                flexDirection: "row",
                alignItems: "center",
                justifyContent: "space-between",
                paddingVertical: 14,
                paddingHorizontal: 18,
                borderBottomWidth: 1,
                borderBottomColor: COLORS.SAND_DARK.hex,
                backgroundColor: "rgba(247,244,238,0.95)",
              }}
            >
              <View style={{ flex: 1, paddingRight: 12 }}>
                <Text
                  style={{
                    fontFamily: FONTS.bodySemiBold,
                    fontSize: 14,
                    color: COLORS.FOREST.hex,
                    letterSpacing: 0.2,
                  }}
                  numberOfLines={1}
                >
                  {title}
                </Text>
                {subtitle ? (
                  <Text
                    style={{
                      marginTop: 2,
                      fontFamily: FONTS.body,
                      fontSize: 11,
                      color: "rgba(28,43,26,0.5)",
                    }}
                    numberOfLines={1}
                  >
                    {subtitle}
                  </Text>
                ) : null}
              </View>
              <Pressable
                onPress={onClose}
                accessibilityRole="button"
                accessibilityLabel="Close"
                style={({ hovered }) => [
                  {
                    width: 30,
                    height: 30,
                    alignItems: "center",
                    justifyContent: "center",
                    borderRadius: 4,
                    borderWidth: 1,
                    borderColor: "rgba(28,43,26,0.12)",
                    backgroundColor: "transparent",
                  },
                  isWeb && hovered
                    ? { backgroundColor: STUDENT_THEME.accentTintSoft, borderColor: STUDENT_THEME.accentBorderHover }
                    : null,
                  isWeb ? ({ cursor: "pointer" } as const) : null,
                ]}
              >
                <Svg width={12} height={12} viewBox="0 0 12 12" fill="none">
                  <Path
                    d="M2 2L10 10M10 2L2 10"
                    stroke={COLORS.FOREST.hex}
                    strokeWidth={1.4}
                    strokeLinecap="round"
                  />
                </Svg>
              </Pressable>
            </View>
            <ScrollView
              style={{ flex: 1 }}
              contentContainerStyle={{ flexGrow: 1 }}
              showsVerticalScrollIndicator
            >
              {children}
            </ScrollView>
          </Pressable>
        </View>
      </Pressable>
    </RNModal>
  );
}

export const HeaderOverlayTokens = {
  FOREST: COLORS.FOREST.hex,
  AMBER: COLORS.AMBER.hex,
  LINEN: COLORS.LINEN.hex,
  SAND_DARK: COLORS.SAND_DARK.hex,
};
